import { ENV_CONFIG } from '@/envConfig';

/**
 * Progressive geocoding fallback. Installers frequently enter a cadastral parcel
 * number as the street, which OpenStreetMap does not know — dropping the street
 * still lands on the right town, which the user can then correct on the map.
 */
export const GEOCODE_TIERS = ['full', 'cityPostal', 'cityOnly'] as const;
export type GeocodeTier = (typeof GEOCODE_TIERS)[number];

/**
 * Environment-derived values are exposed as getters so they stay live: Storybook
 * and tests override `ENV_CONFIG` after import, and module-scope consumers (the
 * rate limiters) must observe the override.
 */
export const GEOLOCATION_CONFIG = {
  nominatim: {
    get baseUrl() {
      return ENV_CONFIG.nominatimBaseUrl;
    },
    /** OSM policy: max 1 req/s. 1100 ms leaves headroom for clock skew. */
    get minIntervalMs() {
      return ENV_CONFIG.geoRateLimitMs;
    },
    limit: 1,
  },
  elevation: {
    get baseUrl() {
      return ENV_CONFIG.elevationBaseUrl;
    },
    get minIntervalMs() {
      return ENV_CONFIG.geoRateLimitMs;
    },
  },
  tiles: {
    get url() {
      return ENV_CONFIG.mapTileUrl;
    },
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  cache: {
    /** Nominatim policy requires client-side caching of results. */
    gcTimeMs: 24 * 60 * 60 * 1000,
    /** 4 dp ~ 11 m — far coarser than the elevation DEM, so a click's jitter still hits the cache. */
    elevationKeyDecimals: 4,
  },
  debounce: {
    /** Address BLUR -> auto-locate. Coalesces tab-through; never keystroke-driven. */
    get autoLocateMs() {
      return ENV_CONFIG.geoDebounceMs;
    },
    /** Coordinates settle -> elevation lookup. */
    get coordinatesMs() {
      return ENV_CONFIG.geoDebounceMs;
    },
  },
  map: {
    /** Roughly the centre of the Czech Republic — the primary market. */
    defaultCenter: { latitude: 49.8, longitude: 15.5 },
    defaultZoom: 6,
    buildingZoom: 18,
    areaFallbackZoom: 13,
  },
  precision: {
    buildingPlaceRank: 28,
    streetPlaceRank: 26,
  },
} as const;
